import { useState, useCallback } from 'react';

interface UseCursorPaginationResult {
  page: number;
  pageSize: number;
  cursor: string | undefined;
  cursorHistory: (string | undefined)[];
  handlePageChange: (newPage: number, items: any[]) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  resetPagination: () => void;
}

/**
 * Custom hook for managing cursor-based pagination state
 * @param initialPageSize - Initial page size (default: 5)
 * @returns Pagination state and handlers
 */
export function useCursorPagination(initialPageSize = 5): UseCursorPaginationResult {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);

  const handlePageChange = useCallback(
    (newPage: number, items: any[]) => {
      const currentPage = page;
      if (newPage > currentPage) {
        if (items.length > 0) {
          const newCursor = items[items.length - 1].id;
          setCursorHistory([...cursorHistory, cursor]);
          setCursor(newCursor);
          setPage(newPage);
        }
      } else if (newPage < currentPage) {
        // Moving backward
        const previousCursor = cursorHistory[newPage];
        setCursor(previousCursor);
        setCursorHistory(cursorHistory.slice(0, newPage));
        setPage(newPage);
      }
    },
    [page, cursor, cursorHistory]
  );

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    setCursor(undefined);
    setCursorHistory([]);
  }, []);

  const resetPagination = useCallback(() => {
    setPage(0);
    setCursor(undefined);
    setCursorHistory([]);
  }, []);

  return {
    page,
    pageSize,
    cursor,
    cursorHistory,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
  };
}
